import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  useTheme,
  Paper,
  Divider,
} from "@mui/material";
import { useUser } from "../provider/AuthProvider";
import {
  Person,
  Email,
  Phone,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import PictureEditor from "../components/PictureEditor";
import axios from "axios";
import { useTranslation } from "react-i18next";

const SettingPage = () => {
  const theme = useTheme();
  const { user, setUser } = useUser();
  const { t } = useTranslation();
  // All editable fields including compound names for managers
  const [formData, setFormData] = useState({
    username: "",
    phone: "",
    email: "",
    compoundName: "",
    compoundArabicName: "",
  });
  const [originalData, setOriginalData] = useState({});
  const [changedFields, setChangedFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");

  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old: "",
    new: "",
    confirm: "",
  });
  const [showPassword, setShowPassword] = useState(false);


  useEffect(() => {
    setFormData({
      username: user?.username || "",
      phone: user?.phone || "",
      email: user?.email || "",
      compoundName: user?.compoundName || "",
      compoundArabicName: user?.compoundArabicName || "",
    });
    setOriginalData({
      username: user?.username || "",
      phone: user?.phone || "",
      email: user?.email || "",
      compoundName: user?.compoundName || "",
      compoundArabicName: user?.compoundArabicName || "",
    });
    setAvatarUrl(user?.avatar_url || "");
  }, [user]);

  // Track changes for field backgrounds
  const handleFieldChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setChangedFields((prev) => ({
      ...prev,
      [field]: value !== originalData[field],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const changedData = await handleUpdateUserInformation(
        formData,
        originalData,
        avatarUrl,
        user.avatar_url
      );

      if (changedData) {
        setUser((prev) => ({ ...prev, ...changedData }));
        setOriginalData(formData);
        setChangedFields({});
        setSnackbar({
          open: true,
          message: "Profile updated",
          severity: "success",
        });
      }
    } catch {
      setSnackbar({ open: true, message: "Update failed", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setChangedFields({});
    setAvatarUrl(user.avatar_url);
  };

  const handleUpdateUserInformation = async (
    formData,
    originalData,
    avatarUrl,
    originalAvatarUrl
  ) => {
    try {
      const changedData = {};
      [
        "username",
        "phone",
        "email",
        "compoundName",
        "compoundArabicName",
      ].forEach((key) => {
        if (formData[key] !== originalData[key]) {
          changedData[key] = formData[key];
        }
      });
      if (avatarUrl && avatarUrl !== originalAvatarUrl) {
        changedData.avatar_url = avatarUrl;
      }
      if (Object.keys(changedData).length === 0) {
        setSnackbar({
          open: true,
          message: "No changes to update.",
          severity: "info",
        });
        return;
      }
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/updateManagerAccount`,
        changedData
      );
      return changedData;
    } catch (err) {
      console.error("Update error:", err);
      throw err;
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordData.old || !passwordData.new || !passwordData.confirm) {
      return setSnackbar({
        open: true,
        message: "Please fill in all fields",
        severity: "warning",
      });
    }

    if (passwordData.new !== passwordData.confirm) {
      return setSnackbar({
        open: true,
        message: "Passwords do not match",
        severity: "warning",
      });
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/change-password`,
        {
          oldPassword: passwordData.old,
          newPassword: passwordData.new,
          confirmPassword: passwordData.confirm,
        }
      );

      setSnackbar({
        open: true,
        message: "Password changed successfully",
        severity: "success",
      });

      setPasswordDialog(false);
      setPasswordData({ old: "", new: "", confirm: "" });
    } catch (err) {
      console.error("Password change failed:", err);
      setSnackbar({
        open: true,
        message:
          err.response?.data?.error ||
          "Failed to change password. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Box className="main-container mt-4">
      <Box display="flex" justifyContent="center">
        <Paper
          elevation={4}
          sx={{ p: 2, width: "50%" }}
          className="notranslate"
        >
          <Box
            mt={4}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Grid container spacing={2} justifyContent="center">
              <PictureEditor
                initialImage={avatarUrl}
                onImageChange={async (croppedUrl) => {
                  try {
                    setAvatarUploading(true);
                    await handleUpdateUserInformation(
                      formData,
                      originalData,
                      croppedUrl,
                      avatarUrl
                    );
                    setAvatarUrl(croppedUrl);
                    setUser((prev) => ({ ...prev, avatar_url: croppedUrl }));
                    setSnackbar({
                      open: true,
                      message: "Avatar updated",
                      severity: "success",
                    });
                  } catch (error) {
                    console.error("Error uploading avatar:", error);
                    setSnackbar({
                      open: true,
                      message: "Avatar update failed",
                      severity: "error",
                    });
                  } finally {
                    setAvatarUploading(false);
                  }
                }}
                size={200}
              />

              {avatarUploading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    bgcolor: "rgba(255,255,255,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {/* Main Profile Fields */}
              <TextField
                label={t("profile.username")}
                className="notranslate"
                fullWidth
                name="username"
                value={formData.username}
                onChange={handleFieldChange("username")}
                sx={{
                  ...(changedFields.username
                    ? { backgroundColor: "rgba(255, 255, 0, 0.08)" }
                    : {}),
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled>
                        <Person />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label={t("profile.phone")}
                className="notranslate"
                fullWidth
                name="phone"
                value={formData.phone}
                onChange={handleFieldChange("phone")}
                sx={{
                  ...(changedFields.phone
                    ? { backgroundColor: "rgba(255, 255, 0, 0.08)" }
                    : {}),
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled>
                        <Phone />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                dir="ltr"
              />

              <TextField
                label={t("profile.email")}
                className="notranslate"
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleFieldChange("email")}
                sx={{
                  ...(changedFields.email
                    ? { backgroundColor: "rgba(255, 255, 0, 0.08)" }
                    : {}),
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled>
                        <Email />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* If Manager: Compound Names */}
              {user?.role === "MANAGER" && (
                <>
                  <Divider sx={{ mb: 1, mt: 2 }} />
                  <TextField
                    label={t("profile.compoundname_en")}
                    className="notranslate"
                    fullWidth
                    name="compoundName"
                    value={formData.compoundName}
                    onChange={handleFieldChange("compoundName")}
                    sx={{
                      ...(changedFields.compoundName
                        ? { backgroundColor: "rgba(255, 255, 0, 0.08)" }
                        : {}),
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton disabled>
                            <Person />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label={t("profile.compoundname_ar")}
                    className="notranslate"
                    fullWidth
                    name="compoundArabicName"
                    value={formData.compoundArabicName}
                    onChange={handleFieldChange("compoundArabicName")}
                    sx={{
                      ...(changedFields.compoundArabicName
                        ? { backgroundColor: "rgba(255, 255, 0, 0.08)" }
                        : {}),
                    }}
                    inputProps={{ dir: "rtl", lang: "ar" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton disabled>
                            <Person />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}
            </Grid>

            <Box display="flex" gap={2} mt={4}>
              <Button
                variant="contained"
                color="primary"
                disabled={saving || !Object.values(changedFields).some(Boolean)}
                onClick={handleSave}
              >
                {saving ? <CircularProgress size={20} /> : t("profile.save")}
              </Button>
              {Object.values(changedFields).some(Boolean) && (
                <Button onClick={handleCancel}>{t("profile.cancel")}</Button>
              )}
              <Button onClick={() => setPasswordDialog(true)}>
                {t("profile.change_password")}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {["old", "new", "confirm"].map((key) => (
            <TextField
              key={key}
              margin="normal"
              fullWidth
              label={
                key === "old"
                  ? "Old Password"
                  : key === "new"
                  ? "New Password"
                  : "Confirm Password"
              }
              type={showPassword ? "text" : "password"}
              value={passwordData[key]}
              onChange={(e) =>
                setPasswordData((p) => ({ ...p, [key]: e.target.value }))
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((s) => !s)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePasswordChange}>
            Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingPage;
